import { FC, RefObject, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { v1 as uuidV1 } from 'uuid';
// mui
import { Divider, Grid } from '@mui/material';
import CustomTooltip from 'components/custom-tooltip';
import ActionButton from 'components/buttons';
// components
import SelectionOptionItem from '../selection-option-item';
import SelectionOptionContent from '../selection-option-content';
// icons
// constants
import { ISelectionFormValues } from '../../../utils/types';
// styles
import { styles } from './styles';

interface IProps {
  isProjectSelection?: boolean;
  isSelectionApproved?: boolean;
  isDescriptionEditDisable?: boolean;
  projectId: number;
  parentRef: RefObject<{ resetOptions: () => void }>;
}

const SelectionOptions: FC<IProps> = ({
  isProjectSelection = true,
  isSelectionApproved = false,
  isDescriptionEditDisable,
  projectId,
  parentRef,
}) => {
  const form = useFormContext<Pick<ISelectionFormValues, 'options'>>();

  const errors = form.formState.errors;

  const { fields, append, move, remove } = useFieldArray({
    control: form.control,
    name: 'options',
    keyName: 'key',
  });

  const [activeOptionId, setActiveOptionId] = useState<string>(
    fields.find(({ isSelected }) => isSelected)?.id ?? fields[0]?.id
  );

  const activeOptionIndex = fields.findIndex(({ id }) => id === activeOptionId);

  const addOption = async () => {
    const isValid = await form.trigger('options');
    if (isValid) {
      const newOptionId = uuidV1();

      append({
        id: newOptionId,
        uploads: [],
        orderNo: fields.length,
        quantity: 1,
        ...(isProjectSelection && { isSelected: false }),
        variants: [],
      });

      setActiveOptionId(newOptionId);
    }
  };

  const deleteOption = useCallback(
    (index: number) => {
      if (fields.length === 1) {
        return;
      }

      if (index === 0) {
        setActiveOptionId(fields[index + 1].id);
      } else {
        setActiveOptionId(fields[index - 1].id);
      }

      remove(index);
    },
    [fields]
  );

  const activateOption = (index: number) => {
    const optionId = fields[index]?.id;
    if (optionId) {
      setActiveOptionId(optionId);
    }
  };

  const moveOption = (dragIndex: number, hoverIndex: number) => {
    move(dragIndex, hoverIndex);
  };

  const isAddButtonDisabled =
    isSelectionApproved ||
    (errors?.options?.length && errors.options.some((errorsItem) => !!errorsItem));

  const tooltipTitle =
    isAddButtonDisabled && !isSelectionApproved
      ? 'Please fill in all required fields before adding another option.'
      : '';

  const resetOptions = () => {
    const newOption = {
      id: uuidV1(),
      orderNo: 0,
      uploads: [],
      quantity: 1,
      isSelected: false,
    };

    form.reset({
      ...form.getValues(),
      options: [newOption],
    });

    setActiveOptionId(newOption.id);
  };

  useImperativeHandle(parentRef, () => ({
    resetOptions,
  }));

  return (
    <>
      <Grid>
        <Divider sx={{ borderColor: '#f3f4f9' }} />
        <Grid sx={styles.container}>
          <Grid sx={styles.optionsWrapper}>
            <SelectionOptionItem
              fields={fields}
              isSelectionApproved={isSelectionApproved}
              isAddButtonDisabled={isAddButtonDisabled}
              tooltipTitle={tooltipTitle}
              activeOptionId={activeOptionId}
              activeOptionIndex={activeOptionIndex}
              errorsOptions={errors?.options}
              moveOption={moveOption}
              activateOption={activateOption}
              addOption={addOption}
            />
          </Grid>
        </Grid>

        <Grid sx={{ background: '#F2F4FF', padding: '12px', borderRadius: '12px' }}>
          <SelectionOptionContent
            deleteOption={deleteOption}
            isDeleteOptionVisible={fields.length > 1 && !isSelectionApproved}
            optionIndex={activeOptionIndex}
            projectId={projectId}
            key={activeOptionId}
            isDescriptionEditDisable={isDescriptionEditDisable}
            isSelectionApproved={isSelectionApproved}
            isProjectSelection={isProjectSelection}
          />
        </Grid>
        <CustomTooltip placement="top" title={tooltipTitle}>
          <Grid sx={styles.addNewOption}>
            <ActionButton
              disabled={isAddButtonDisabled}
              type="button"
              label="Add New Option"
              variant="secondary"
              onClick={addOption}
            />
          </Grid>
        </CustomTooltip>
      </Grid>
    </>
  );
};

export default SelectionOptions;
